import { enrichAIFlowWithImpactScore } from '@src/aiFlow/impactScore';
import { analytics } from '@src/utility/analytics';
import { toJS } from 'mobx';

import { LogManager } from '../utility/Logger';
import { equalsIgnoringCase, removePunctuation } from '../utility/strings';
import { AIClipProcessor } from './AIClipProcessor';

import type { AIFlowStep } from '../aiFlow/AIFlowStep';
import type { SourcePlatform, StoryVideo, Video } from '../types.ts/story';
import type { TranscriptElement } from '../types.ts/transcript';
import type { AudienceOption } from '@src/components/common/AudienceSelector';
import type { GoalOption } from '@src/components/common/GoalSelector';
import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';
import type { RootStore } from '@src/stores-v2/RootStore';
import type { ClipProducerPreset } from '@src/types.ts/ai_prompts';

export type ClipFragment = {
  title: string;
  text: string;
  duration: number;
  startTime: number;
  src: string;
  muxPlaybackId?: string;
  platform?: string;
  theme: string;
  themeDisplayName?: string;
  transcriptPosition: {
    startIndex: number;
    endIndex: number;
    // in case startIndex/endIndex is derived from alternative transcript
    // source (e.g. save as clip might use karaoke lines)
    relevantElements?: TranscriptElement[];
  };
  impactScore?: Record<string, number>; // Optional, if related prompt is used
  speakerName?: string; // Optional speaker name for filtered clips
  speakerLabel?: string; // Optional speaker label for filtered clips
  isReel?: boolean; // Optional, if this clip is a reel: compilation of multiple clips
};

const MAX_WORDS_PER_CHUNK = 2000;
const MAX_WORDS_OVERLAP = 300;

const logger = LogManager.getLogger('AIClipProducer');

const getFlowTitleAndCategoryByTheme = (
  clipProducerPresets: ClipProducerPreset[] | null,
  theme: string,
) => {
  return clipProducerPresets?.find(preset => preset.title === theme)!;
};

export const getDurationBySocialPlatform = (platform?: string) => {
  switch (platform) {
    case 'tiktok':
      return [5, 180];
    case 'instagram':
      return [20, 70];
    case 'youtube':
      return [50, 420];
    case 'youtube-shorts':
      return [10, 60];
    case 'facebook':
      return [20, 70];
    case 'twitter':
      return [10, 120];
    case 'linkedin':
      return [10, 120];
    default:
      return [10, 120];
  }
};

const getRangeForDuration = (duration: number) =>
  ({
    7: [3, 12],
    15: [6, 29],
    30: [16, 49],
    60: [34, 80],
    90: [65, 120],
    120: [75, 150],
  })[duration] || [0.7 * duration, 1.3 * duration];

function chunkTranscription(
  words: string[],
  chunkSize: number,
  overlap: number,
): { text: string; index: number }[] {
  const chunks: { text: string; index: number }[] = [];

  for (let i = 0; i < words.length; i += chunkSize - overlap) {
    const chunkWords = words.slice(i, i + chunkSize);
    chunks.push({
      text: chunkWords.join(' '),
      index: i, // word index — useful for mapping later
    });
    if (i + chunkSize >= words.length) break;
  }

  return chunks;
}

function deduplicateClips(clips: ClipFragment[]): ClipFragment[] {
  const seen = new Set<string>();
  const uniqueClips: ClipFragment[] = [];

  for (const clip of clips) {
    const key = `${clip.transcriptPosition.startIndex}-${clip.transcriptPosition.endIndex}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueClips.push(clip);
    }
  }

  return uniqueClips;
}

function createClipResultMapper({
  transcriptionElements,
  originalVideo,
  platform,
  theme,
  themeDisplayName,
  durationMin,
  durationMax,
  speakers,
}: {
  transcriptionElements: TranscriptElement[];
  originalVideo: StoryVideo;
  platform?: string;
  theme: string;
  themeDisplayName?: string;
  durationMin: number;
  durationMax: number;
  speakers: Speaker[];
}): (flowContext: any, stepContext: AIFlowStep) => (ClipFragment | null)[] {
  return (flowContext: any, stepContext: AIFlowStep) => {
    const prevResponseFragmentsNumber =
      stepContext.context.previousResponseFragmentsNumber || 0;
    const prevResultFragments = (stepContext.context.previousResultFragments ||
      []) as ClipFragment[];

    const resultParts = (stepContext.lastResponse?.slice(
      prevResponseFragmentsNumber,
    ) || []) as {
      title: string;
      text_fragment: string;
      impact_score: Record<string, number>;
    }[];

    const NUMBER_OF_WORDS_TO_MATCH = 5;

    if (resultParts.length === 0) {
      if (stepContext.doneStreaming) {
        stepContext.context.previousResultFragments = [];
        stepContext.context.previousResponseFragmentsNumber = 0;
      }
      return prevResultFragments;
    }

    stepContext.context.previousResponseFragmentsNumber =
      stepContext.lastResponse?.length || 0;

    const resultFragments: (ClipFragment | null)[] = resultParts.map(
      (part, index) => {
        const partIndex = prevResponseFragmentsNumber + index;
        const beginning = stripPunctuationFromEnds(
          part.text_fragment
            .split(' ')
            .slice(0, NUMBER_OF_WORDS_TO_MATCH)
            .join(' '),
        );
        const ending = stripPunctuationFromEnds(
          part.text_fragment
            .split(' ')
            .slice(-NUMBER_OF_WORDS_TO_MATCH)
            .join(' '),
        );

        stepContext.log(
          `Fragment ${partIndex}: \nbeginning: ${beginning}, \nending: ${ending}`,
        );

        const {
          startTime: beginningTime,
          transcriptPosition: beginningTranscriptPosition,
        } = getFragmentPosition(beginning, transcriptionElements) || {};
        if (beginningTime == null || !beginningTranscriptPosition) {
          stepContext.log(`Fragment ${partIndex} has no beginning position.`);
          return null;
        }

        const {
          endTime: endingTime,
          transcriptPosition: endingTranscriptPosition,
        } = getFragmentPosition(ending, transcriptionElements) || {};
        if (endingTime == null || !endingTranscriptPosition) {
          stepContext.log(`Fragment ${partIndex} has no ending position.`);
          return null;
        }

        const fragmentDuration = endingTime - beginningTime;
        const fragmentWordsCount = part.text_fragment.split(' ').length;

        const duplicateIndexCheck = prevResultFragments.findIndex(
          fragment =>
            fragment.transcriptPosition.startIndex ===
              beginningTranscriptPosition.startIndex &&
            fragment.transcriptPosition.endIndex ===
              endingTranscriptPosition.endIndex,
        );
        if (duplicateIndexCheck !== -1) {
          stepContext.log(
            `Fragment ${partIndex} is a duplicate of ${duplicateIndexCheck}`,
          );
          return null;
        }

        if (
          fragmentDuration >= durationMin &&
          fragmentDuration <= durationMax
        ) {
          stepContext.log(
            `Fragment ${partIndex} has duration ${fragmentDuration}s (ok); words: ${fragmentWordsCount}`,
          );

          // Check if this fragment has a predominant speaker
          const fragmentElements = transcriptionElements.slice(
            beginningTranscriptPosition.startIndex,
            Math.min(
              endingTranscriptPosition.endIndex + 1,
              transcriptionElements.length,
            ),
          );

          // Count speaker occurrences in this fragment
          const speakerCounts: Record<string, number> = {};
          let predominantSpeaker: string | null = null;
          let predominantSpeakerLabel: string | null = null;

          // Count speakers in the fragment
          fragmentElements.forEach(element => {
            if (element.speaker_label) {
              speakerCounts[element.speaker_label] =
                (speakerCounts[element.speaker_label] || 0) + 1;
            }
          });
          // Find predominant speaker if any
          if (Object.keys(speakerCounts).length > 0) {
            const entries = Object.entries(speakerCounts);
            entries.sort((a, b) => b[1] - a[1]); // Sort by count descending

            // If one speaker has at least 60% of the elements, consider them predominant
            const totalElements = fragmentElements.filter(
              element => element.speaker_label,
            ).length;
            const [topSpeakerLabel, topSpeakerCount] = entries[0];

            if (topSpeakerCount / totalElements >= 0.6) {
              predominantSpeakerLabel = topSpeakerLabel;

              // Use the getSpeakerNameFromLabel helper to format the speaker name
              if (predominantSpeakerLabel) {
                predominantSpeaker =
                  speakers.find(
                    speaker => speaker.speakerLabel === predominantSpeakerLabel,
                  )?.name || null;
              }
            }
          }

          return {
            title: part.title || '',
            text: part.text_fragment,
            duration: fragmentDuration,
            startTime: beginningTime,
            src:
              originalVideo?.video?.mp4UrlLow ||
              originalVideo?.video?.mp4UrlMedium ||
              originalVideo.url,
            muxPlaybackId: originalVideo?.video?.muxPlaybackId,
            platform,
            theme,
            themeDisplayName,
            transcriptPosition: {
              startIndex: beginningTranscriptPosition.startIndex,
              endIndex: Math.min(
                endingTranscriptPosition.endIndex + 1,
                transcriptionElements.length - 1,
              ),
            },
            impactScore: part.impact_score,
            // Include speaker information if available
            ...(predominantSpeaker && { speakerName: predominantSpeaker }),
            ...(predominantSpeakerLabel && {
              speakerLabel: predominantSpeakerLabel,
            }),
          };
        }
        stepContext.log(
          `Fragment ${partIndex} has invalid duration ${fragmentDuration}s`,
        );

        return null;
      },
    );

    const notNullFragments = resultFragments.filter(Boolean) as ClipFragment[];
    const newResultFragments = prevResultFragments.concat(notNullFragments);
    stepContext.context.previousResultFragments = newResultFragments;

    if (stepContext.doneStreaming) {
      stepContext.context.previousResultFragments = [];
      stepContext.context.previousResponseFragmentsNumber = 0;
    }

    return newResultFragments;
  };
}

export const produceClipsChunked = async (params: {
  videoCreator: IVideoCreatorStore;
  submitTranscriptionText: string;
  transcriptionText: string;
  originalVideo: StoryVideo;
  originalDuration: number;
  transcriptionElements: TranscriptElement[];
  duration: number | null;
  theme: string;
  customTheme?: string;
  themeDisplayName?: string;
  platform?: string;
  onClipsProduced?: (clips: ClipFragment[]) => void;
  abortToken: { id: string; aborted: boolean };
  rootStore: RootStore;
  speakers?: any[]; // Optional array of speaker data from SpeakerSelector
  goal?: GoalOption | null; // Optional goal selection
  audiences?: AudienceOption[]; // Optional audience selection
}) => {
  const {
    videoCreator,
    submitTranscriptionText,
    transcriptionText,
    transcriptionElements,
    duration,
    originalDuration,
    theme,
    customTheme,
    platform,
    originalVideo,
    onClipsProduced,
    abortToken,
    rootStore,
    speakers = [],
    goal = null,
    audiences = [],
    themeDisplayName,
  } = params;

  analytics.track('produce_clips', {
    storyId: videoCreator.story?.id,
    storyTitle: videoCreator.story?.title,
    theme,
    platform,
    duration,
  });

  let [durationMin, durationMax] = getDurationBySocialPlatform(platform);
  if (duration) {
    [durationMin, durationMax] = getRangeForDuration(duration);
  }

  const originalVideoWords = transcriptionText.split(' ');
  const newVideoWords = Math.round(
    (originalVideoWords.length * ((durationMin + durationMax) / 2)) /
      originalDuration,
  );
  const [clipLengthMin, clipLengthMax] = [durationMin, durationMax].map(
    duration =>
      Math.round((originalVideoWords.length * duration) / originalDuration),
  );

  const preset =
    customTheme && theme === 'Custom'
      ? rootStore.templatedPromptsStore.customClipProducerPreset
      : getFlowTitleAndCategoryByTheme(
          rootStore.templatedPromptsStore.clipProducerPresets,
          theme,
        );

  const CHUNK_WORDS = MAX_WORDS_PER_CHUNK;
  // Math.min(
  //   clipLengthMax * MAX_CLIPS_PER_CHUNK,
  //   MAX_WORDS_PER_CHUNK,
  // );
  const OVERLAP_WORDS = Math.min(clipLengthMax, MAX_WORDS_OVERLAP);
  const chunks = chunkTranscription(
    submitTranscriptionText.split(' '),
    CHUNK_WORDS,
    OVERLAP_WORDS,
  );

  const clipProducerAiFlow =
    await rootStore.datoClientStore.aiFlowRepository.load(preset);
  if (!clipProducerAiFlow) {
    console.error('No clip producer AI flow found');
    return [];
  }
  // Prepare an array of promises for all chunk flows
  const flowPromises = chunks.map(async ({ text: chunkText }, chunkIndex) => {
    if (abortToken.aborted) return [];
    const localAiFlowClone = clipProducerAiFlow.clone();

    let bufferedResults: ClipFragment[] = [];

    // title to log: orgName - storyName - theme - chunk 1/total
    localAiFlowClone.title = `${videoCreator.organization
      ?.title} - ${videoCreator.story?.title} - ${preset.title} - chunk ${
      chunkIndex + 1
    }/${chunks.length}`;

    // Format goal and audiences
    let goalContext = '';
    if (goal && goal.id !== 'none') {
      // get aiprompt for goal
      const goalPrompt =
        await rootStore.datoClientStore.aiPromptRepository.getPromptByTitle(
          'Clip Producer | Context | Goal',
        );
      if (goalPrompt) {
        goalContext = `${goalPrompt.description}\n\n${goal.label}: ${goal.finalPrompt}`;
      }
    }
    let audiencesContext = '';
    if (audiences && audiences.length > 0) {
      // get aiprompt for audiences
      const audiencePrompt =
        await rootStore.datoClientStore.aiPromptRepository.getPromptByTitle(
          'Clip Producer | Context | Audiences',
        );
      if (audiencePrompt) {
        audiencesContext = `${audiencePrompt.description}\n\n${audiences
          .map(a => `${a.label}: ${a.finalPrompt}`)
          .join('\n\n')}`;
      }
    }

    localAiFlowClone.context = {
      storyTranscription: chunkText,
      storyLength: chunkText.split(' ').length,
      storyDuration: originalDuration,
      chunksTotal: chunks.length,
      socialPlatform: platform,
      clipLengthMin,
      clipLengthMax,
      clipLength: newVideoWords,
      clipDurationMin: durationMin,
      clipDurationMax: durationMax,
      clipTheme: customTheme || '',
      goalContext,
      audiencesContext,
    };

    await enrichAIFlowWithImpactScore(localAiFlowClone, rootStore);

    localAiFlowClone.steps[0].stream = 'object';
    localAiFlowClone.steps[0].mapper = createClipResultMapper({
      transcriptionElements,
      originalVideo,
      platform,
      theme,
      themeDisplayName,
      durationMin,
      durationMax,
      speakers,
    });

    localAiFlowClone.steps[0].onPartialResult = (
      result: (ClipFragment | null)[],
    ) => {
      if (abortToken.aborted) throw new Error('Aborted');
      bufferedResults = result.filter(Boolean) as ClipFragment[];
      if (chunkIndex === 0 && result.length > 0) {
        // only first chunk is streamed, other chunks are buffered
        onClipsProduced && onClipsProduced(bufferedResults);
      }
    };

    // small delay to avoid overwhelming the AI service and network
    const delayMs = 1500 * chunkIndex;
    await new Promise(resolve => setTimeout(resolve, delayMs));

    await localAiFlowClone.run();
    if (abortToken.aborted) throw new Error('Aborted');
    return bufferedResults;
  });

  // Wait for each result in order and emit clips in order
  const allClips: ClipFragment[] = [];
  for (const flowPromise of flowPromises) {
    const chunkClips = await flowPromise;
    allClips.push(...chunkClips);
    if (onClipsProduced && chunkClips.length > 0) {
      onClipsProduced(allClips);
    }
  }

  const uniqueClips = deduplicateClips(allClips);
  const sortedClips = uniqueClips.sort((a, b) => a.startTime - b.startTime);
  return sortedClips;
};

export const produceTestClips = async (
  params: any & {
    originalVideo: StoryVideo;
    originalDuration: number;
    transcriptionElements: TranscriptElement[];
    theme: string;
    platform?: string;
    onClipsProduced?: (clips: ClipFragment[]) => void;
  } & {
    maxClips?: number;
    produceIncrementally?: boolean;
    produceInterval?: number;
  },
) => {
  const {
    transcriptionElements,
    originalDuration,
    theme,
    platform,
    originalVideo,
    onClipsProduced,
    maxClips = 10,
    produceIncrementally,
  } = params;

  const allClips: ClipFragment[] = [];
  const transcriptElementsPerClip = Math.floor(
    transcriptionElements.length / maxClips,
  );

  while (
    allClips.length < maxClips &&
    transcriptElementsPerClip * allClips.length < transcriptionElements.length
  ) {
    console.log('Push new clip');
    allClips.push({
      title: `Clip ${allClips.length + 1}`,
      text: '',
      duration: originalDuration / maxClips,
      startTime: allClips.length * (originalDuration / maxClips),
      src:
        originalVideo?.video?.mp4UrlLow ||
        originalVideo?.video?.mp4UrlMedium ||
        originalVideo.url,
      platform,
      theme,
      transcriptPosition: {
        startIndex: transcriptElementsPerClip * allClips.length,
        endIndex: Math.min(
          transcriptElementsPerClip * (allClips.length + 1),
          transcriptionElements.length - 1,
        ),
      },
    });
    if (produceIncrementally) {
      // wait produceInterval ms
      await new Promise(resolve => {
        setTimeout(() => {
          resolve(null);
        }, params.produceInterval || 1000);
      });
      console.log('Return new clip');
      onClipsProduced && onClipsProduced(allClips);
    }
  }

  if (onClipsProduced && allClips.length > 0) {
    onClipsProduced(allClips);
  }

  return allClips;
};

function getFragmentPosition(
  fragment: string,
  transcriptElements?: TranscriptElement[],
): {
  transcriptPosition: {
    startIndex: number;
    endIndex: number;
  };
  duration: number;
  startTime: number;
  endTime: number;
} | null {
  const targetSentence = removePunctuation(fragment);
  // loop through all transcript elements and track the index of the target sentence across multiple elements
  if (!transcriptElements || !targetSentence) return null;

  let targetSentenceCurrIndex = 0;
  let currElementIndex = 0;
  let targetSentenceElementStartIndex = 0;
  let targetSentenceElementEndIndex = 0;
  let found = false;

  while (currElementIndex < (transcriptElements?.length - 1 || 0)) {
    const currentElement = transcriptElements[currElementIndex];
    const currElementValue = currentElement.value;

    // if element contains the start of the target sentence save it as the start of the index
    if (
      currElementValue &&
      currentElement?.state !== 'removed' &&
      currentElement?.state !== 'cut'
    ) {
      // if currElementValue is punctuation skip
      if (currElementValue && currElementValue.match(/^[.,;!?]$/)) {
        currElementIndex++;
        continue;
      } else if (
        equalsIgnoringCase(
          targetSentence.substring(
            targetSentenceCurrIndex,
            targetSentenceCurrIndex + currElementValue.length,
          ),
          currElementValue,
        )
      ) {
        if (targetSentenceCurrIndex === 0) {
          targetSentenceElementStartIndex = currElementIndex;
        }
        targetSentenceCurrIndex += currElementValue.length;
      } else {
        targetSentenceCurrIndex = 0;
      }
    }
    if (targetSentenceCurrIndex === targetSentence.length) {
      targetSentenceElementEndIndex = currElementIndex;
      found = true;
      break;
    }
    currElementIndex++;
  }

  // RETURN if not found
  if (!found) return null;

  let ts = null;
  let end_ts = null;

  // loop through all transcript elements and track the index of the target sentence across multiple elements
  currElementIndex = targetSentenceElementStartIndex;
  while (currElementIndex <= targetSentenceElementEndIndex) {
    const currElementFinal = transcriptElements[currElementIndex];

    if (currElementFinal) {
      if (ts === null && currElementFinal.ts != null) {
        ts = currElementFinal.ts - (currElementFinal.buffer_before_ts || 0);
      }
      if (currElementFinal.end_ts) {
        end_ts =
          currElementFinal.end_ts + (currElementFinal.buffer_after_ts || 0);
      }
    }
    currElementIndex++;
  }

  if (ts == null || end_ts == null) return null;

  return {
    transcriptPosition: {
      startIndex: targetSentenceElementStartIndex,
      endIndex: targetSentenceElementEndIndex,
    },
    duration: end_ts - ts,
    startTime: ts,
    endTime: end_ts,
  };
}

function stripPunctuationFromEnds(text: string) {
  return text.replaceAll(
    /^[\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+|[\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+$/g,
    '',
  );
}

export async function saveProducedClips(
  videoCreator: IVideoCreatorStore,
  clips: ClipFragment[],
  sourcePlatform: SourcePlatform,
  options?: {
    parallel?: boolean;
  },
): Promise<void> {
  const originalSource = getOriginalSource(videoCreator);
  const originalVideo = getOriginalVideo(videoCreator);
  const nowStart = performance.now();
  // Parallel path (feature flagged)
  if (options?.parallel) {
    const [videoToLoad] = await saveClipsInParallel(
      originalVideo,
      clips,
      videoCreator,
      sourcePlatform,
    );
    const nowClipsSaved = performance.now();
    if (!videoToLoad?.id) {
      logger.warn('saveProducedClips: no video to load after parallel save');
    } else if (sourcePlatform === 'creator-studio') {
      await videoCreator.loadVideo(videoToLoad.id);
      logger.debug('saveProducedClips: new clip loaded after parallel save', {
        videoId: videoToLoad.id,
      });
    }
    const nowEnd = performance.now();
    logger.info('saveProducedClips: parallel save timings', {
      totalClips: clips.length,
      totalTimeMs: nowEnd - nowStart,
      timeToSaveClipsMs: nowClipsSaved - nowStart,
      timeToLoadVideoMs: nowEnd - nowClipsSaved,
    });
    return;
  }
  if (sourcePlatform === 'creator-studio') {
    // same source should be used for all produced clips
    for (const clip of clips) {
      await saveClipInCreator(
        videoCreator,
        clip,
        originalVideo,
        originalSource,
      );
    }
    const nowEnd = performance.now();
    logger.info('saveProducedClips: sequential save timings', {
      totalClips: clips.length,
      totalTimeMs: nowEnd - nowStart,
    });
  } else {
    for (const clip of clips) {
      await videoCreator.createNewVideoForContentClip(originalVideo, clip);

      copyClipToCurrentVideo(videoCreator, clip);
      await videoCreator.saveStoryAndVideo(false, false);
    }
    const nowEnd = performance.now();
    logger.info('saveProducedClips: sequential save timings', {
      totalClips: clips.length,
      totalTimeMs: nowEnd - nowStart,
    });
  }
}

async function saveClipsInParallel(
  originalVideo: Video,
  clips: ClipFragment[],
  videoCreator: IVideoCreatorStore,
  sourcePlatform: SourcePlatform,
): Promise<Video[]> {
  if (!originalVideo) throw new Error('Original video id missing');
  const results = await Promise.allSettled(
    clips.map(clip => {
      const saver = new AIClipProcessor(
        clip,
        videoCreator,
        originalVideo,
        sourcePlatform,
      );
      return saver.save();
    }),
  );
  const newVideos: Video[] = results
    .filter((r): r is PromiseFulfilledResult<Video> => r.status === 'fulfilled')
    .map(r => r.value);
  logger.info('saveClipsInParallel: done creating videos from clips', {
    total: results.length,
    fulfilled: newVideos.length,
  });
  results.forEach((result, idx) => {
    if (result.status === 'rejected') {
      logger.error('saveClipsInParallel: failed to save clip', {
        clipIndex: idx,
        reason: result.reason,
        clip: clips[idx],
      });
    }
  });
  await videoCreator.addMultipleVideosToStory(newVideos);
  logger.info('saveClipsInParallel: done adding videos to story', {
    storyId: videoCreator.story?.id,
    newVideos: newVideos.map(v => v.id),
  });
  return newVideos;
}

export async function saveClipFromTranscript(
  videoCreator: IVideoCreatorStore,
  clip: any,
  options?: {
    parallel?: boolean; // doesn't matter for single clip, but kept for API consistency
  },
) {
  logger.debug('saveClipFromTranscript: start', { clip, options });

  await videoCreator.saveStoryAndVideo();
  logger.debug('saveClipFromTranscript: current story and video saved');

  const originalVideo = videoCreator.currentVideo;
  const originalSource = videoCreator.rootStore.videoSourceStore.source;
  if (!originalSource || !originalVideo) {
    throw new Error('No video source found to create clip');
  }

  if (options?.parallel) {
    let videoToLoad: Video | null = null;
    videoCreator.isSaving = true;
    try {
      [videoToLoad] = await saveClipsInParallel(
        getOriginalVideo(videoCreator),
        [clip],
        videoCreator,
        'creator-studio',
      );
    } catch (error) {
      logger.error('saveClipFromTranscript: error in parallel save', { error });
    } finally {
      videoCreator.isSaving = false;
    }
    if (videoToLoad?.id) {
      await videoCreator.loadVideo(videoToLoad.id);
      logger.debug('saveClipFromTranscript: new clip loaded', {
        videoId: videoToLoad.id,
      });
    } else {
      logger.warn('saveClipFromTranscript: no video to load');
    }
  } else {
    await saveClipInCreator(videoCreator, clip, originalVideo, originalSource);
    logger.debug('saveClipFromTranscript: new clip saved sequentially');
  }
}

async function saveClipInCreator(
  videoCreator: IVideoCreatorStore,
  clip: any,
  originalVideo: Video,
  originalSource: any,
) {
  logger.debug('saveClipInCreator: start', {
    clip,
    originalVideoId: originalVideo.id,
  });

  const newTitle = clip.title || `${originalVideo.title} - ${clip.theme}`;
  await videoCreator.createNewVideoFromSource(
    newTitle,
    originalSource,
    originalVideo.transcriptionChanges,
    originalVideo.transcriptionSnapshot,
    originalVideo.karaokeData,
    originalVideo.extraElementData,
    'creator-studio',
    originalVideo.aspectRatio,
  );
  logger.debug('saveClipInCreator: createNewVideoFromSource completed');

  await videoCreator.cropTranscriptAndVideoTo(
    clip.transcriptPosition.startIndex,
    clip.transcriptPosition.endIndex,
  );
  logger.debug('saveClipInCreator: cropTranscriptAndVideoTo completed');

  await prepareForRendering(videoCreator);

  copyClipToCurrentVideo(videoCreator, clip);
  logger.debug('saveClipInCreator: copyClipToCurrentVideo completed');

  await videoCreator.saveStoryAndVideo();
  logger.debug('saveClipInCreator: completed');
}

async function prepareForRendering(videoCreator: IVideoCreatorStore) {
  if (
    videoCreator.rootStore.editorStore?.headlessEnabled &&
    videoCreator.rootStore.editorStore?.enableHeadlessEditorWave3 &&
    videoCreator.rootStore.editorStore?.editor
  ) {
    videoCreator.rootStore.editorStore.editor.commandAutoCalculateTimelineDuration();
  } else {
    await videoCreator.setDuration(null);
  }
  logger.debug('prepareForRendering: setDuration completed');

  videoCreator.timelineStore.setShouldCalcTimelineScale(true);
}

function copyClipToCurrentVideo(
  videoCreator: IVideoCreatorStore,
  clip: ClipFragment,
) {
  videoCreator.currentVideo!.clipJson = {
    startTime: clip.startTime,
    duration: clip.duration,
    muxPlaybackId: clip.muxPlaybackId,
    impactScore: clip.impactScore,
    isReel: clip.isReel,
  };
}

function getOriginalSource(
  videoCreator: IVideoCreatorStore,
): Record<string, any> {
  let originalSource: Record<string, any> | undefined =
    videoCreator.rootStore.videoSourceStore.source;
  if (!originalSource) {
    originalSource = videoCreator.currentVideo?.videoSource;
  }
  if (!originalSource) {
    throw new Error('No video source found');
  }
  return originalSource;
}

function getOriginalVideo(videoCreator: IVideoCreatorStore): Video {
  return toJS(videoCreator.currentVideo!);
}

export async function saveClipsAsReel(
  videoCreator: VideoCreatorStore,
  clips: ClipFragment[],
  sourcePlatform: SourcePlatform,
): Promise<void> {
  if (sourcePlatform === 'creator-studio') {
    await saveClipsAsReelInCreator(videoCreator, clips);
  } else {
    await saveClipsAsReelInContentStudio(videoCreator, clips);
  }
}

async function saveClipsAsReelInCreator(
  videoCreator: VideoCreatorStore,
  clips: ClipFragment[],
) {
  const originalSource = getOriginalSource(videoCreator);
  const originalVideo = getOriginalVideo(videoCreator);
  const newTitle = getReelTitle(videoCreator);

  await videoCreator.createNewVideoFromSource(
    newTitle,
    originalSource,
    originalVideo.transcriptionChanges,
    originalVideo.transcriptionSnapshot,
    originalVideo.karaokeData,
    originalVideo.extraElementData,
    'creator-studio',
    originalVideo.aspectRatio,
  );

  await videoCreator.cropTranscriptAndVideoInMultiplePlaces(
    clipsToRanges(clips),
  );

  if (
    videoCreator.rootStore.editorStore?.headlessEnabled &&
    videoCreator.rootStore.editorStore?.enableHeadlessEditorWave3 &&
    videoCreator.rootStore.editorStore?.editor
  ) {
    videoCreator.rootStore.editorStore.editor.commandAutoCalculateTimelineDuration();
  } else {
    await videoCreator.setDuration(null);
  }

  videoCreator.timelineStore.setShouldCalcTimelineScale(true);

  copyClipToCurrentVideo(videoCreator, combineClipsIntoOne(clips, newTitle));
  await videoCreator.saveStoryAndVideo();
}

async function saveClipsAsReelInContentStudio(
  videoCreator: VideoCreatorStore,
  clips: ClipFragment[],
) {
  const originalVideo = getOriginalVideo(videoCreator);
  const newTitle = getReelTitle(videoCreator);
  const combinedClip = combineClipsIntoOne(clips, newTitle);

  await videoCreator.createNewVideoForContentClipsReel(
    originalVideo,
    combinedClip,
    clipsToRanges(clips),
  );

  copyClipToCurrentVideo(videoCreator, combinedClip);
  await videoCreator.saveStoryAndVideo(false, false);
}

function getReelTitle(videoCreator: VideoCreatorStore): string {
  return `${videoCreator.story!.title} - Compilation`;
}

function combineClipsIntoOne(
  clips: ClipFragment[],
  newTitle: string,
): ClipFragment {
  return clips.reduce(
    (acc, clip) => {
      acc.text += `${clip.text}\n`;
      acc.duration += clip.duration;
      acc.startTime = Math.min(acc.startTime, clip.startTime);
      acc.src = clip.src;
      acc.platform = clip.platform;
      acc.theme = clip.theme;
      acc.transcriptPosition = {
        startIndex: Math.min(
          acc?.transcriptPosition?.startIndex,
          clip.transcriptPosition.startIndex,
        ),
        endIndex: Math.max(
          acc?.transcriptPosition?.endIndex,
          clip.transcriptPosition.endIndex,
        ),
      };
      acc.title = newTitle;
      return acc;
    },
    {
      text: '',
      duration: 0,
      startTime: Infinity,
      transcriptPosition: {
        startIndex: Infinity,
        endIndex: -1,
      },
      isReel: true,
    } as ClipFragment,
  );
}

function clipsToRanges(
  clips: ClipFragment[],
): { fromElement: number; toElement: number }[] {
  return clips.map(clip => ({
    fromElement: clip.transcriptPosition.startIndex,
    toElement: clip.transcriptPosition.endIndex,
  }));
}
